import { useEffect, useMemo, type KeyboardEvent } from "react";
import { strokeFor, viewBoxOf, type Bounds, type MapView } from "@/lib/map/view";
import type { LayerBucket } from "@/lib/map/layers";
import type { MapCamera } from "./use-map-view";
import { TonePattern } from "./tone-pattern";

/** One drawable thing on the map — a state at the national level, a county below it. */
export interface CanvasFeature {
  id: string;
  name: string;
  /** Projected SVG path on the shared 960x600 viewBox. */
  d: string;
  bbox: Bounds;
  /** Where this feature's own page lives. Used as the href, so the map works with no JS. */
  href: string;
  bucket: LayerBucket;
  /** Full sentence for assistive tech, e.g. "Texas: Found here. Opens the Texas page." */
  ariaLabel: string;
}

/**
 * One fill per legend bucket: flat colour with the quiet texture from TonePattern, the
 * same fill the home page's map uses. Sizes are divided by the zoom so the texture stays
 * the same size on screen as the map scales (the viewBox scales everything).
 */
function BucketPatterns({ buckets, view }: { buckets: LayerBucket[]; view: MapView }) {
  const size = strokeFor(view, 9);
  const line = strokeFor(view, 1.05);
  return (
    <defs>
      {buckets.map((b) => (
        <TonePattern
          key={b.key}
          id={`fill-${b.key}`}
          tone={b.tone}
          fill={b.fill}
          ink={b.foreground}
          size={size}
          line={line}
        />
      ))}
    </defs>
  );
}

export interface MapCanvasProps {
  features: CanvasFeature[];
  /**
   * Non-interactive outlines drawn underneath — the surrounding states while you are
   * inside one, so a county view still reads as a place on a map.
   */
  contextPaths?: string[];
  buckets: LayerBucket[];
  camera: MapCamera;
  /** Feature the user has opened a panel for. */
  selectedId: string | null;
  /** Feature that currently owns the single tab stop (roving tabindex). */
  activeId: string | null;
  onSelect: (feature: CanvasFeature) => void;
  onActivate: (id: string) => void;
  /** Step the roving focus. Bound to Arrow keys while a feature has focus. */
  onStep: (delta: number) => void;
  /** Accessible name for the whole canvas, e.g. "Map of Texas counties". */
  label: string;
  describedBy?: string;
}

export function MapCanvas({
  features,
  contextPaths,
  buckets,
  camera,
  selectedId,
  activeId,
  onSelect,
  onActivate,
  onStep,
  label,
  describedBy,
}: MapCanvasProps) {
  const { view, dragging, surfaceRef, pointerHandlers, onKeyDown } = camera;
  const hairline = strokeFor(view, 0.6);
  const selectedStroke = strokeFor(view, 2.2);

  // Keep the roving tab stop on an element that exists: when the feature set changes
  // (drill-down, layer switch) the old id may be gone.
  useEffect(() => {
    if (activeId && !features.some((f) => f.id === activeId)) onActivate(features[0]?.id ?? "");
  }, [features, activeId, onActivate]);

  const rendered = useMemo(
    () =>
      features.map((f) => ({
        ...f,
        isSelected: f.id === selectedId,
        isActive: f.id === activeId,
      })),
    [features, selectedId, activeId],
  );

  const handleFeatureKey = (event: KeyboardEvent<Element>, feature: CanvasFeature) => {
    // While a feature has focus, the arrows walk the feature list; panning stays on
    // the canvas itself, which is the previous tab stop.
    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      onStep(1);
      return;
    }
    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      onStep(-1);
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect(feature);
    }
  };

  return (
    <svg
      ref={surfaceRef}
      viewBox={viewBoxOf(view)}
      preserveAspectRatio="xMidYMid meet"
      // role="application" so a screen reader passes the arrow keys through to the
      // pan handler instead of consuming them for its own cursor. The always-visible
      // feature list below the map is the guaranteed path either way.
      role="application"
      aria-roledescription="Interactive map"
      aria-label={label}
      aria-describedby={describedBy}
      tabIndex={0}
      className="h-full w-full touch-none select-none focus-visible:outline-2 focus-visible:outline-offset-[-3px] focus-visible:outline-ring"
      style={{ cursor: dragging ? "grabbing" : "grab" }}
      onKeyDown={(e) => {
        if (onKeyDown(e)) e.preventDefault();
      }}
      {...pointerHandlers}
    >
      <BucketPatterns buckets={buckets} view={view} />
      {contextPaths && contextPaths.length > 0 && (
        <g aria-hidden style={{ pointerEvents: "none" }}>
          {contextPaths.map((d) => (
            <path
              key={d.slice(0, 24)}
              d={d}
              fill="var(--muted)"
              stroke="var(--border)"
              strokeWidth={hairline}
              opacity={0.55}
            />
          ))}
        </g>
      )}
      {rendered.map((f) => (
        <a
          key={f.id}
          href={f.href}
          aria-label={f.ariaLabel}
          aria-current={f.isSelected ? "true" : undefined}
          tabIndex={f.isActive ? 0 : -1}
          onFocus={() => onActivate(f.id)}
          onKeyDown={(e) => handleFeatureKey(e, f)}
          onClick={(e) => {
            // With JS this selects and drills down; with JS off the href just works.
            e.preventDefault();
            if (dragging) return;
            onSelect(f);
          }}
          className="focus-visible:outline-none"
        >
          <path
            d={f.d}
            fill={`url(#fill-${f.bucket.key})`}
            stroke={f.isSelected || f.isActive ? "var(--foreground)" : "var(--background)"}
            strokeWidth={f.isSelected || f.isActive ? selectedStroke : hairline}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            style={{ pointerEvents: "auto" }}
          />
        </a>
      ))}
    </svg>
  );
}
